import React from "react";

import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, Info, ListChecks, X } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Mono eyebrow above the title, e.g. "Quick look". */
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  /** Right-aligned action buttons under a divider. */
  footer?: React.ReactNode;
  /** Read to screen readers on open (visually hidden); defaults to the eyebrow. */
  description?: string;
}

/** Portfolio modal (mock v4): Esc / backdrop close, focus trapped in the dialog. */
const PortfolioModal: React.FC<Props> = ({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  description,
}) => (
  <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/40" />
      <Dialog.Content
        // Focus the dialog itself on open, not its first button (the close
        // button showed a focus ring as if it had been clicked).
        onOpenAutoFocus={e => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).focus();
        }}
        tabIndex={-1}
        aria-modal="true"
        className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-xl focus:outline-none"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {eyebrow}
            </div>
            <Dialog.Title className="mt-0.5 text-lg font-bold text-gray-900">
              {title}
            </Dialog.Title>
            {/* F-UF-F30: every dialog says what it is to a screen reader. */}
            <Dialog.Description className="sr-only">
              {description || eyebrow}
            </Dialog.Description>
          </div>
          <Dialog.Close
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            <X className="h-4 w-4" aria-hidden />
          </Dialog.Close>
        </div>
        {children}
        {footer && (
          <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
            {footer}
          </div>
        )}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

/**
 * The standard briefing questions, in order. Mirrors backend
 * `services/plan_parsing/briefing_questions.py::DEFAULT_SET`; the status
 * endpoint doesn't return them.
 */
export const STANDARD_BRIEFING_QUESTIONS = [
  "What are the top 1–2 strategic priorities or risks to align to immediately?",
  "Do we currently have recurring weekly or strategic account calls for this customer?",
  "Would a bi-weekly executive-focused touchpoint make sense?",
  "Are QBRs / Executive Business Reviews scheduled?",
  "What is the most efficient way to stay aligned?",
];

/** "EB answers these 5 standard briefing questions…" strip + its modal. */
export const BriefingQuestionsStrip: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const n = STANDARD_BRIEFING_QUESTIONS.length;
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-[#8538d4]">
          <ListChecks className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 text-sm text-gray-700">
          EB answers these{" "}
          <span className="font-semibold">{n} standard briefing questions</span>{" "}
          on every plan
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 rounded-md text-sm font-semibold text-[#8538d4] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          View standard questions{" "}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <PortfolioModal
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Briefing questions"
        title={`Your ${n} briefing questions`}
        footer={
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg bg-[#974ae4] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            Close
          </button>
        }
      >
        <ol className="space-y-2">
          {STANDARD_BRIEFING_QUESTIONS.map((q, i) => (
            <li
              key={q}
              className="flex gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800"
            >
              <span className="mt-0.5 font-mono text-xs text-gray-400">
                Q{i + 1}
              </span>
              <span>{q}</span>
            </li>
          ))}
        </ol>
        <p className="mt-4 flex items-start gap-1.5 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {/* Not "coming soon": published briefings carry the answers (QA-03). */}
          Each account&apos;s answers appear in its Quick look and on its plan
          once the briefing is drafted.
        </p>
      </PortfolioModal>
    </>
  );
};

export default PortfolioModal;
